// D3 Utility functions for BodyOS data visualizations
import * as d3 from 'd3'
import {
  colors,
  fonts,
  tooltipConfig,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'

/**
 * Create a responsive SVG container with proper viewBox
 */
export function createSvg(container, width, height, margins = defaultMargins) {
  const svg = d3.select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('class', 'd3-visualization')
    .style('width', '100%')
    .style('height', 'auto')
    .style('font-family', fonts.family)

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left}, ${margins.top})`)

  return { svg, g, innerWidth: width - margins.left - margins.right, innerHeight: height - margins.top - margins.bottom }
}

/**
 * Create X axis with BodyOS styling
 */
export function createXAxis(g, scale, height, options = {}) {
  const {
    tickFormat = null,
    ticks = null,
    label = null,
    rotate = 0
  } = options

  const axis = d3.axisBottom(scale)

  if (tickFormat) axis.tickFormat(tickFormat)
  if (ticks) axis.ticks(ticks)

  const axisG = g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0, ${height})`)
    .call(axis)

  // Style the axis
  axisG.selectAll('line')
    .attr('stroke', colors.axis)
    .attr('stroke-width', 2)

  axisG.selectAll('path')
    .attr('stroke', colors.axis)
    .attr('stroke-width', 2)

  axisG.selectAll('text')
    .attr('fill', colors.textMuted)
    .attr('font-size', fonts.sizes.sm)
    .attr('font-family', fonts.family)

  if (rotate) {
    axisG.selectAll('text')
      .attr('transform', `rotate(${rotate})`)
      .attr('text-anchor', rotate > 0 ? 'start' : 'end')
      .attr('dx', rotate > 0 ? '0.5em' : '-0.5em')
      .attr('dy', '0.5em')
  }

  if (label) {
    g.append('text')
      .attr('class', 'x-axis-label')
      .attr('x', scale.range()[1] / 2)
      .attr('y', height + 45)
      .attr('text-anchor', 'middle')
      .attr('fill', colors.text)
      .attr('font-size', fonts.sizes.base)
      .attr('font-weight', fonts.weights.medium)
      .text(label.toUpperCase())
  }

  return axisG
}

/**
 * Create Y axis with BodyOS styling
 */
export function createYAxis(g, scale, options = {}) {
  const {
    tickFormat = null,
    ticks = null,
    label = null,
    gridLines = true,
    width = 0
  } = options

  const axis = d3.axisLeft(scale)

  if (tickFormat) axis.tickFormat(tickFormat)
  if (ticks) axis.ticks(ticks)

  const axisG = g.append('g')
    .attr('class', 'y-axis')
    .call(axis)

  // Style the axis
  axisG.selectAll('line')
    .attr('stroke', colors.axis)
    .attr('stroke-width', 2)

  axisG.selectAll('path')
    .attr('stroke', colors.axis)
    .attr('stroke-width', 2)

  axisG.selectAll('text')
    .attr('fill', colors.textMuted)
    .attr('font-size', fonts.sizes.sm)
    .attr('font-family', fonts.family)

  // Add grid lines
  if (gridLines && width > 0) {
    const gridAxis = d3.axisLeft(scale)
      .tickSize(-width)
      .tickFormat('')

    if (ticks) gridAxis.ticks(ticks)

    const gridG = g.insert('g', ':first-child')
      .attr('class', 'y-grid')
      .call(gridAxis)

    gridG.selectAll('line')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1)

    gridG.selectAll('path')
      .attr('stroke', 'none')
  }

  if (label) {
    g.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', 'rotate(-90)')
      .attr('x', -scale.range()[0] / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('fill', colors.text)
      .attr('font-size', fonts.sizes.base)
      .attr('font-weight', fonts.weights.medium)
      .text(label.toUpperCase())
  }

  return axisG
}

/**
 * Create a tooltip element
 */
export function createTooltip(_container) {
  // Use fixed positioning for accurate placement with page coordinates
  const tooltip = d3.select('body')
    .append('div')
    .attr('class', 'd3-tooltip')
    .style('position', 'fixed')
    .style('visibility', 'hidden')
    .style('background-color', tooltipConfig.backgroundColor)
    .style('color', tooltipConfig.textColor)
    .style('padding', `${tooltipConfig.padding}px`)
    .style('font-family', tooltipConfig.fontFamily)
    .style('font-size', `${tooltipConfig.fontSize}px`)
    .style('border', `${tooltipConfig.borderWidth}px solid ${tooltipConfig.borderColor}`)
    .style('pointer-events', 'none')
    .style('z-index', '10000')
    .style('white-space', 'nowrap')
    .style('max-width', '300px')

  return tooltip
}

/**
 * Show tooltip with content (plain text, safe from XSS)
 */
export function showTooltip(tooltip, content, event) {
  tooltip
    .text(content)
    .style('visibility', 'visible')
    .style('left', `${event.clientX + 15}px`)
    .style('top', `${event.clientY - 10}px`)
}

/**
 * Show tooltip with HTML content
 * Only use with trusted internal content (not user input)
 */
export function showTooltipHtml(tooltip, htmlContent, event) {
  tooltip
    .html(htmlContent)
    .style('visibility', 'visible')
    .style('left', `${event.clientX + 15}px`)
    .style('top', `${event.clientY - 10}px`)
}

/**
 * Hide tooltip
 */
export function hideTooltip(tooltip) {
  tooltip.style('visibility', 'hidden')
}

/**
 * Create a legend
 */
export function createLegend(g, items, options = {}) {
  const {
    x = 0,
    y = -30,
    direction = 'horizontal',
    itemSpacing = null // Auto-calculate if not provided
  } = options

  const legend = g.append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(${x}, ${y})`)

  // For horizontal legends, calculate positions based on actual text widths
  const positions = []
  let currentX = 0

  items.forEach((item, _i) => {
    positions.push(currentX)

    if (direction === 'horizontal') {
      // Estimate text width: ~7px per character for monospace at size 10
      const textWidth = item.label.toUpperCase().length * 7
      const itemWidth = 20 + textWidth + 24 // rect + gap + text + padding
      currentX += itemSpacing || itemWidth
    }
  })

  items.forEach((item, i) => {
    const itemG = legend.append('g')
      .attr('class', 'legend-item')
      .attr('transform', direction === 'horizontal' ?
        `translate(${positions[i]}, 0)` :
        `translate(0, ${i * 24})`)

    // Symbol (square for isometric aesthetic)
    itemG.append('rect')
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', item.color)
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1)

    // Label
    itemG.append('text')
      .attr('x', 20)
      .attr('y', 10)
      .attr('fill', colors.text)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)
      .text(item.label.toUpperCase())
  })

  return legend
}

/**
 * Create gradient definition for area charts
 */
export function createGradient(svg, id, color, opacity1 = 0.3, opacity2 = 0) {
  const defs = svg.append('defs')

  const gradient = defs.append('linearGradient')
    .attr('id', id)
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')

  gradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', color)
    .attr('stop-opacity', opacity1)

  gradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', color)
    .attr('stop-opacity', opacity2)

  return `url(#${id})`
}

/**
 * Animate elements with stagger
 */
export function animateStagger(selection, duration = animationConfig.duration, stagger = animationConfig.stagger) {
  return selection
    .attr('opacity', 0)
    .transition()
    .duration(duration)
    .delay((d, i) => i * stagger)
    .attr('opacity', 1)
}

/**
 * Format numbers for display
 */
export function formatNumber(value, decimals = 0) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toFixed(decimals)
}

/**
 * Format percentage
 */
export function formatPercent(value, decimals = 0) {
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Wrap long text
 */
export function wrapText(selection, maxWidth) {
  selection.each(function wrapTextElement() {
    const text = d3.select(this)
    const words = text.text().split(/\s+/).reverse()
    const lineHeight = 1.1
    const y = text.attr('y')
    const dy = parseFloat(text.attr('dy') || 0)

    let word = words.pop()
    let line = []
    let lineNumber = 0
    let tspan = text.text(null)
      .append('tspan')
      .attr('x', 0)
      .attr('y', y)
      .attr('dy', `${dy}em`)

    while (word) {
      line.push(word)
      tspan.text(line.join(' '))
      if (tspan.node().getComputedTextLength() > maxWidth) {
        line.pop()
        tspan.text(line.join(' '))
        line = [word]
        lineNumber += 1
        tspan = text.append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', `${lineNumber * lineHeight + dy}em`)
          .text(word)
      }
      word = words.pop()
    }
  })
}

/**
 * Get responsive dimensions based on container
 */
export function getResponsiveDimensions(container, aspectRatio = 16 / 9) {
  const containerWidth = container.clientWidth || defaultDimensions.width
  const width = Math.min(containerWidth, defaultDimensions.width)
  const height = width / aspectRatio

  return { width, height }
}

/**
 * Debounce function for resize handlers
 */
export function debounce(func, wait = 250) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default {
  createSvg,
  createXAxis,
  createYAxis,
  createTooltip,
  showTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  createGradient,
  animateStagger,
  formatNumber,
  formatPercent,
  wrapText,
  getResponsiveDimensions,
  debounce
}
